import React, { useState, useEffect, Fragment } from "react";
import EventCard from "./EventCard";
import NewsCard from './NewsCard';
import { Box, FormControl, InputLabel, MenuItem, Tab, Typography, Select, Skeleton, Card, Grid, Chip } from "@mui/material";
import { Event, Participants, Item } from './Event';
import News from './News';
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { useTranslation } from 'react-i18next';


function Content({
    axiosEvents, axiosEventItems, axiosEventParticipants, axiosEventParticipantItems, axiosEventThumbnails, axiosEventFiles,
    axiosNews, axiosNewsThumbnails,
    loadingEvents, loadingNews,
    date, dayjs, selectedDate, clearSelectedDate, token, user
}) {
    const [tabsValue, setTabsValue] = useState('news');
    const [eventFilter, setEventFilter] = useState('upcoming');
    const [eventSort, setEventSort] = useState('chronological');
    const [newsSort, setNewsSort] = useState('new');
    const { t } = useTranslation();

    const handleTabs = (event, newValue) => {
        setTabsValue(newValue);
    }
    const handleEventFilter = (event) => {
        setEventFilter(event.target.value);
    }
    const handleEventSort = (event) => {
        setEventSort(event.target.value);
    }
    const handleNewsSort = (event) => {
        setNewsSort(event.target.value);
    }

    // Switch to events tab when a date is selected
    useEffect(() => {
        if (selectedDate) {
            setTabsValue('events');
        }
    }, [selectedDate]);

    let news = [];
    if (!loadingNews) {
        for (let i = 0; i < axiosNews.length; i++) {
            news[i] = new News(axiosNews[i].id, axiosNews[i].userId, axiosNews[i].addressId, axiosNews[i].title, axiosNews[i].body, axiosNewsThumbnails[i], axiosNews[i].newsTimestamp, axiosNews[i].created, axiosNews[i].modified, axiosNews[i].translations);
        }
    }

    let events = [];
    if (!loadingEvents) {
        for (let i = 0; i < axiosEvents.length; i++) {
            events[i] = new Event(axiosEvents[i].id, axiosEvents[i].creatorId, axiosEvents[i].addressId, axiosEventThumbnails[i], axiosEventFiles[i], axiosEvents[i].title, axiosEvents[i].body, axiosEvents[i].capacity, axiosEvents[i].eventType, axiosEvents[i].created, axiosEvents[i].modified, axiosEvents[i].translations);

            events[i].setDuration(axiosEvents[i].duration);

            if (axiosEventItems[i]) {
                if (axiosEventItems[i][0]?.eventId === axiosEvents[i].id) {
                    let itemArray = axiosEventItems[i];
                    for (let j = 0; j < itemArray.length; j++) {
                        events[i].pushItem(new Item(itemArray[j].id, axiosEvents[i].id, itemArray[j].name, itemArray[j].quantity, 'kg', itemArray[j].created, itemArray[j].modified));
                    }
                }
            }

            if (axiosEventParticipants[i]) {
                if (axiosEventParticipants[i][0]?.eventId === axiosEvents[i].id) {
                    const participantsArray = axiosEventParticipants[i];
                    for (let j = 0; j < participantsArray.length; j++) {
                        events[i].pushParticipant(new Participants(j, events[i].id, participantsArray[j].firstname, participantsArray[j].lastname, participantsArray[j].email, participantsArray[j].role, participantsArray[j].code, participantsArray[j].comments, participantsArray[j].created, participantsArray[j].modified));

                        if (axiosEventParticipantItems) {
                            for (let k = 0; k < axiosEventParticipantItems.length; k++) {
                                if (events[i].participants[j].email === axiosEventParticipantItems[k].email) {
                                    events[i].participants[j].pushEventItemId(axiosEventParticipantItems[k].eventItemId);

                                    const itemsArray = axiosEventItems[i];
                                    for (let l = 0; l < itemsArray?.length; l++) {
                                        if (itemsArray[l].id === axiosEventParticipantItems[k].eventItemId) {
                                            events[i].participants[j].pushItems(new Item(itemsArray[l].id, events[i].id, itemsArray[l].name, axiosEventParticipantItems[k].quantity, 'lbs', itemsArray[l].created, itemsArray[l].modified));
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }

            events[i].setStartDate(dayjs(axiosEvents[i]?.eventTimestamp));

            let endDate = events[i].date.start;
            if (events[i].duration.days) {
                endDate = endDate.add(events[i].duration.days, 'day')
            }
            if (events[i].duration.hours) {
                endDate = endDate.add(events[i].duration.hours, 'hour')
            }
            if (events[i].duration.minutes) {
                endDate = endDate.add(events[i].duration.minutes, 'minute')
            }
            events[i].setEndDate(endDate);

            if (date < events[i].date.start) {
                events[i].setStatus('upcoming');
            } else if (date > events[i].date.end) {
                events[i].setStatus('past');
            } else if (date > events[i].date.start && date < events[i].date.end) {
                events[i].setStatus('current');
            }
        }
    }

    if (eventFilter === 'upcoming') {
        events = events.filter((event) => event.status === 'upcoming');
    } else if (eventFilter === 'past') {
        events = events.filter((event) => event.status === 'past');
    } else if (eventFilter === 'current') {
        events = events.filter((event) => event.status === 'current');
    }

    // Filter by selected calendar date
    if (selectedDate) {
        events = events.filter((event) => {
            const eventDate = dayjs(axiosEvents.find(e => e.id === event.id)?.eventTimestamp);
            return eventDate.isSame(selectedDate, 'day');
        });
    }

    // Sort events - chronological shows earliest first (ascending), reverse shows latest first (descending)
    if (eventSort === 'chronological') {
        for (let i = 0; i < events.length; i++) {
            for (let j = i + 1; j < events.length; j++) {
                if (events[i].date.start > events[j].date.start) {
                    const tempValue = events[i];
                    events[i] = events[j];
                    events[j] = tempValue;
                }
            }
        }
    } else if (eventSort === 'reverse') {
        for (let i = 0; i < events.length; i++) {
            for (let j = i + 1; j < events.length; j++) {
                if (events[i].date.start < events[j].date.start) {
                    const tempValue = events[i];
                    events[i] = events[j];
                    events[j] = tempValue;
                }
            }
        }
    }

    for (let i = 0; i < events.length; i++) {
        let startDate = (events[i].date.start).toDate();
        let year = (startDate.getFullYear()).toString();
        startDate = startDate.toString();
        let index = (startDate).indexOf(year);

        let time = startDate.slice(index + 5, index + 10);
        let hour = Number(time.slice(0, 2));
        if (hour > 12) {
            hour -= 12;
            time = hour.toString() + time.slice(2, 5);
            startDate = startDate.slice(0, index + 5);
            events[i].setStartDate(startDate + time + ' PM');
        } else {
            time = hour.toString() + time.slice(2, 5);
            startDate = startDate.slice(0, index + 5);
            events[i].setStartDate(startDate + time + ' AM');
        }

        let endDate = (events[i].date.end).toDate();
        year = (endDate.getFullYear()).toString();
        endDate = endDate.toString();
        index = (endDate).indexOf(year);

        time = endDate.slice(index + 5, index + 10);
        hour = Number(time.slice(0, 2));
        if (hour > 12) {
            hour -= 12;
            time = hour.toString() + time.slice(2, 5);
            endDate = endDate.slice(0, index + 5);
            events[i].setEndDate(endDate + time + ' PM');
        } else {
            time = hour.toString() + time.slice(2, 5);
            endDate = endDate.slice(0, index + 5);
            events[i].setEndDate(endDate + time + ' AM');
        }
    }

    if (newsSort === 'new') {
        for (let i = 0; i < news.length; i++) {
            for (let j = i + 1; j < news.length; j++) {
                if (news[i].newsTimestamp < news[j].newsTimestamp) {
                    const tempValue = news[i];
                    news[i] = news[j];
                    news[j] = tempValue;
                }
            }
        }
    } else if (newsSort === 'old') {
        for (let i = 0; i < news.length; i++) {
            for (let j = i + 1; j < news.length; j++) {
                if (news[i].newsTimestamp > news[j].newsTimestamp) {
                    const tempValue = news[i];
                    news[i] = news[j];
                    news[j] = tempValue;
                }
            }
        }
    }

    for (let i = 0; i < news.length; i++) {
        let date = (dayjs(news[i].newsTimestamp)).toDate();
        const year = (date.getFullYear()).toString();
        date = date.toString();
        const index = (date).indexOf(year);

        let time = date.slice(index + 5, index + 10);
        let hour = Number(time.slice(0, 2));
        if (hour > 12) {
            hour -= 12;
            time = hour.toString() + time.slice(2, 5);
            date = date.slice(0, index + 5);
            news[i].setNewsTimestamp(date + time + ' PM');
        } else {
            time = hour.toString() + time.slice(2, 5);
            date = date.slice(0, index + 5);
            news[i].setNewsTimestamp(date + time + ' AM');
        }
    }

    return (
        <TabContext value={tabsValue}>
            <Box sx={{
                borderBottom: 1,
                borderColor: '#E0E0E0',
                mb: 2
            }}>
                <TabList
                    onChange={handleTabs}
                    aria-label={'News & Event Tabs'}
                    sx={{
                        '& .MuiTab-root': {
                            fontFamily: "'Montserrat', sans-serif",
                            fontWeight: 500,
                            fontSize: '1rem',
                            textTransform: 'none',
                            color: '#49454F',
                            padding: '0.75rem 1.5rem',
                            minHeight: '48px',
                            '&:hover': {
                                color: '#6750A4',
                                backgroundColor: 'rgba(103, 80, 164, 0.04)'
                            },
                            '&.Mui-selected': {
                                color: '#6750A4',
                                fontWeight: 600
                            }
                        },
                        '& .MuiTabs-indicator': {
                            backgroundColor: '#6750A4',
                            height: '3px',
                            borderRadius: '3px 3px 0 0'
                        }
                    }}
                >
                    <Tab label={t('EventsPage.News')} value={'news'} />
                    <Tab label={t('EventsPage.Events')} value={'events'} />
                </TabList>
            </Box>
            <TabPanel value={'news'} sx={{ padding: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel sx={{ fontFamily: "'Montserrat', sans-serif" }}>{t('EventsPage.Sort')}</InputLabel>
                        <Select
                            value={newsSort}
                            label={t('EventsPage.Sort')}
                            onChange={handleNewsSort}
                            sx={{ fontFamily: "'Montserrat', sans-serif", borderRadius: '8px' }}
                        >
                            <MenuItem value={'new'}>{t('EventsPage.Newest First')}</MenuItem>
                            <MenuItem value={'old'}>{t('EventsPage.Oldest First')}</MenuItem>
                        </Select>
                    </FormControl>
                </Box>

                {loadingNews
                    ? <div className="skeletonCard">
                        <Grid container columnGap={3}>
                            <Grid item container direction={'column'} sx={{ flex: 1 }}>
                                <Skeleton variant={'text'} sx={{ fontSize: '2rem' }} animation={'wave'} />
                                <Skeleton variant={'text'} animation={'wave'} />
                                <Skeleton variant={'text'} width="60%" animation={'wave'} />
                            </Grid>
                            <Grid item>
                                <Skeleton variant={'rounded'} width={200} height={140} animation={'wave'} sx={{ borderRadius: '12px' }} />
                            </Grid>
                        </Grid>
                    </div>
                    : <Fragment>
                        {news.length
                            ? <Fragment>
                                {news.map((newspaper) => (
                                    <NewsCard
                                        key={newspaper.id}
                                        id={newspaper.id}
                                        userId={newspaper.userId}
                                        addressId={newspaper.addressId}
                                        title={newspaper.title}
                                        body={newspaper.body}
                                        translations={newspaper.translations}
                                        thumbnail={newspaper.thumbnail}
                                        newsTimestamp={newspaper.newsTimestamp}
                                        created={newspaper.created}
                                        modified={newspaper.modified}
                                        user={user}
                                        token={token}
                                    />
                                ))}
                            </Fragment>
                            : <div className="emptyState">
                                <Typography className="emptyStateText">
                                    {t('EventsPage.No news articles available')}
                                </Typography>
                            </div>
                        }
                    </Fragment>
                }
            </TabPanel>

            <TabPanel value={'events'} sx={{ padding: 0 }}>
                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel sx={{ fontFamily: "'Montserrat', sans-serif" }}>{t('EventsPage.Filter')}</InputLabel>
                        <Select
                            value={eventFilter}
                            label={t('EventsPage.Filter')}
                            onChange={handleEventFilter}
                            sx={{ fontFamily: "'Montserrat', sans-serif", borderRadius: '8px' }}
                        >
                            <MenuItem value={'upcoming'}>{t('EventsPage.Upcoming')}</MenuItem>
                            <MenuItem value={'current'}>{t('EventsPage.Happening Now')}</MenuItem>
                            <MenuItem value={'past'}>{t('EventsPage.Past Events')}</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel sx={{ fontFamily: "'Montserrat', sans-serif" }}>{t('EventsPage.Sort')}</InputLabel>
                        <Select
                            value={eventSort}
                            label={t('EventsPage.Sort')}
                            onChange={handleEventSort}
                            sx={{ fontFamily: "'Montserrat', sans-serif", borderRadius: '8px' }}
                        >
                            <MenuItem value={'chronological'}>{t('EventsPage.Chronological')}</MenuItem>
                            <MenuItem value={'reverse'}>{t('EventsPage.Reverse Order')}</MenuItem>
                        </Select>
                    </FormControl>
                    {selectedDate && (
                        <Chip
                            label={`Events on ${selectedDate.format('MMM D, YYYY')}`}
                            onDelete={clearSelectedDate}
                            sx={{
                                fontFamily: "'Montserrat', sans-serif",
                                fontWeight: 500,
                                borderColor: '#6750A4',
                                color: '#6750A4',
                                '& .MuiChip-deleteIcon': {
                                    color: '#6750A4',
                                    '&:hover': {
                                        color: '#4A3880'
                                    }
                                }
                            }}
                            variant="outlined"
                        />
                    )}
                </Box>

                {loadingEvents
                    ? <div className="skeletonCard">
                        <Grid container columnGap={3}>
                            <Grid item container direction={'column'} sx={{ flex: 1 }}>
                                <Skeleton variant={'text'} sx={{ fontSize: '2rem' }} animation={'wave'} />
                                <Skeleton variant={'text'} animation={'wave'} />
                                <Skeleton variant={'text'} width="60%" animation={'wave'} />
                            </Grid>
                            <Grid item>
                                <Skeleton variant={'rounded'} width={200} height={140} animation={'wave'} sx={{ borderRadius: '12px' }} />
                            </Grid>
                        </Grid>
                    </div>
                    : <Fragment>
                        {events.length
                            ? <Fragment>
                                {events.map((event) => (
                                    <EventCard
                                        key={event.id}
                                        id={event.id}
                                        userId={event.userId}
                                        addressId={event.addressId}
                                        file={event.file}
                                        image={event.photo}
                                        thumbnail={event.thumbnail}
                                        title={event.title}
                                        body={event.body}
                                        created={event.created}
                                        modified={event.modified}
                                        translations={event.translations}
                                        date={event.date}
                                        timezone={event.timezone}
                                        duration={event.duration}
                                        type={event.locationType}
                                        link={event.link}
                                        capacity={event.capacity}
                                        participants={event.participants}
                                        items={event.items}
                                        status={event.status}
                                        token={token}
                                        user={user}
                                    />
                                ))}
                            </Fragment>
                            : <div className="emptyState">
                                <Typography className="emptyStateText">
                                    {t('EventsPage.No events found')}
                                </Typography>
                            </div>
                        }
                    </Fragment>
                }
            </TabPanel>
        </TabContext>
    )
}

export default Content;
